import { ArrowUpRight, Globe2, Info, MapPin, Waves } from "lucide-react";
import { countRange, matches, returningPatterns } from "./model";
import { useObservatoryCopy } from "./copy";
import { ReportingIcon } from "./ReportingIcons";
import { ReportingTrendTable } from "./ReportingTrendTable";
import { ReportingTrendChart, trendSeries } from "./ReportingTrendChart";
import type { ObservatoryView } from "./schema";

export function ReportingSummary({
  view,
  title,
  global = false,
  onThreat,
}: {
  view: ObservatoryView;
  title: string;
  global?: boolean;
  onThreat: (value: string) => void;
}) {
  const { t } = useObservatoryCopy();
  const threats = [...(view.dimensions.attacks ?? [])]
    .sort((a, b) => b.lower - a.lower)
    .slice(0, 3);
  return (
    <section
      className="observatory-panel observatory-summary"
      aria-label={title}
    >
      <header className="observatory-panel-heading">
        <div>
          <h2>
            {global ? (
              <Globe2 size={25} aria-hidden="true" />
            ) : (
              <MapPin size={25} aria-hidden="true" />
            )}
            <strong>{title}</strong>
          </h2>
          <span className="observatory-muted">{t("reportedPatterns")}</span>
        </div>
        <span className="observatory-count-tag">
          <bdi>{countRange(view.cases)}</bdi>
          <small>{t("cases")}</small>
        </span>
      </header>
      <ul className="observatory-threat-list">
        {threats.map((row, i) => (
          <li key={row.label}>
            <button onClick={() => onThreat(row.label)}>
              <ReportingIcon label={row.label} />
              <span>{row.label}</span>
              <bdi className={`volume-${3 - i}`}>{countRange(row)}</bdi>
            </button>
          </li>
        ))}
      </ul>
      {!threats.length && (
        <p className="observatory-inline-empty">{t("noAttacks")}</p>
      )}
      <ReportingTimeline view={view} compact />
    </section>
  );
}

export function ReportingMatrix({
  view,
  query,
  onSelect,
}: {
  view: ObservatoryView;
  query: string;
  onSelect: (community: string, threat: string) => void;
}) {
  const { t } = useObservatoryCopy();
  const cells = view.matrix.filter((row) =>
    matches(query, [row.community, row.threat]),
  );
  const groups = [...new Set(cells.map((v) => v.community))];
  const threats = [...new Set(cells.map((v) => v.threat))];
  const maximum = Math.max(1, ...cells.map((v) => v.lower));
  return (
    <section className="observatory-panel observatory-matrix-panel">
      <h2>{t("matrix")}</h2>
      {!cells.length ? (
        <p role="status">{query ? t("noSearch") : t("noAttacks")}</p>
      ) : (
        <>
          <div className="observatory-table-scroll">
            <table className="observatory-matrix">
              <thead>
                <tr>
                  <th>{t("community")}</th>
                  {threats.map((v) => (
                    <th key={v}>
                      <span>{v}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group}>
                    <th scope="row">
                      <span className="observatory-community-label">
                        <ReportingIcon label={group} />
                        <span>{group}</span>
                      </span>
                    </th>
                    {threats.map((threat) => {
                      const cell = cells.find(
                        (c) => c.community === group && c.threat === threat,
                      );
                      return (
                        <td key={threat}>
                          {cell ? (
                            <button
                              className={`observatory-cell volume-${cell.lower / maximum >= 0.85 ? 3 : cell.lower / maximum >= 0.6 ? 2 : 1}`}
                              aria-label={`${group} · ${threat} · ${countRange(cell)}`}
                              onClick={() => onSelect(group, threat)}
                            >
                              <bdi>{countRange(cell)}</bdi>
                            </button>
                          ) : (
                            <span
                              className="observatory-withheld"
                              aria-label={t("unknown")}
                            >
                              —
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="observatory-volume-legend">
            <span>{t("reportVolume")}</span>
            <span>{t("lower")}</span>
            <i className="volume-1" />
            <i className="volume-2" />
            <i className="volume-3" />
            <span>{t("higher")}</span>
            <span className="observatory-legend-divider">
              — {t("withheld")}
            </span>
          </div>
        </>
      )}
      <p className="observatory-sr-only">{t("matrixHelp")}</p>
    </section>
  );
}

export function ReportingTimeline({
  view,
  compact = false,
}: {
  view: ObservatoryView;
  compact?: boolean;
}) {
  const { t } = useObservatoryCopy();
  const plotted = trendSeries(view);
  const series = plotted.length
    ? plotted
    : (view.dimensions.months ?? []).map((m) => ({
        month: m.label,
        threat: t("all"),
        lower: m.lower,
        upper: m.upper,
      }));
  const names = [...new Set(series.map((r) => r.threat))];
  const contents = (
    <>
      <div className="observatory-panel-heading">
        <h3>{t("timeline")}</h3>
        <span className="observatory-badge">{t("publishedTrends")}</span>
      </div>
      {series.length ? (
        <>
          <ReportingTrendChart view={view} />
          <div className="observatory-series-legend">
            {names.map((v, i) => (
              <span key={v} data-series={i}>
                {v}
              </span>
            ))}
          </div>
          <details className="observatory-data-details">
            <summary>{t("details")}</summary>
            <p className="observatory-panel-note">{t("dateHelp")}</p>
            <ReportingTrendTable rows={series} />
          </details>
        </>
      ) : (
        <p>{t("noMonthlyTrend")}</p>
      )}
    </>
  );
  return compact ? (
    <div className="observatory-inline-timeline">{contents}</div>
  ) : (
    <section className="observatory-panel">{contents}</section>
  );
}

export function RecurringPatterns({
  view,
  onSelect,
}: {
  view: ObservatoryView;
  onSelect?: (threat: string) => void;
}) {
  const { t } = useObservatoryCopy();
  const patterns = returningPatterns(view);
  const selected = patterns[0];
  return (
    <section className="observatory-panel observatory-recurring-panel">
      <h2>{t("recurring")}</h2>
      {selected ? (
        <article className="observatory-returning">
          <header>
            <h3>
              <ReportingIcon label={selected.threat} />
              {selected.threat}: {t("patternReturned")}
            </h3>
          </header>
          <p>
            {t("earlier")}: <bdi>{selected.earlier}</bdi>
            <span aria-hidden="true"> → </span>
            {t("renewed")}: <bdi>{selected.renewed}</bdi>
          </p>
          <div className="observatory-pattern-actions">
            {onSelect && (
              <button
                className="observatory-text-button"
                onClick={() => onSelect(selected.threat)}
              >
                {t("explorePattern")}
                <ArrowUpRight size={14} aria-hidden="true" />
              </button>
            )}
            <details className="observatory-data-details">
              <summary>{t("details")}</summary>
              <ReportingTrendChart
                view={view}
                threat={selected.threat}
                miniature
              />
              <ReportingTrendTable
                rows={view.series.filter((r) => r.threat === selected.threat)}
              />
            </details>
          </div>
        </article>
      ) : (
        <div className="observatory-pattern-empty">
          <Waves size={30} aria-hidden="true" />
          <p role="status">{t("noSignal")}</p>
        </div>
      )}
      {patterns.length > 1 && (
        <details className="observatory-data-details">
          <summary>
            {t("morePatterns")} ({patterns.length - 1})
          </summary>
          {patterns.slice(1).map((p) => (
            <button
              key={p.threat}
              className="observatory-text-button"
              onClick={() => onSelect?.(p.threat)}
            >
              {p.threat} · <bdi>{p.renewed}</bdi>
            </button>
          ))}
        </details>
      )}
      <p className="observatory-panel-note observatory-recurrence-note">
        <Info size={15} aria-hidden="true" />
        {t("recurrenceCaution")}
      </p>
    </section>
  );
}
